/**
 * Mapea errores RPC de Comisiones a mensaje friendly (vos).
 * Preserva `code` interno para Sentry — nunca mostrar raw al usuario.
 */

export type MappedCommissionError = {
  code: string;
  userMessage: string;
  /** Operacional esperado — no capturar como crash. */
  expected?: boolean;
};

const COMMISSION_ERROR_MAP: Array<{
  match: RegExp;
  code: string;
  userMessage: string;
  expected?: boolean;
}> = [
  {
    match: /module_permission_denied|permission denied|not.?allowed/i,
    code: 'commission_permission_denied',
    userMessage: 'No tenés permiso para esta acción de comisiones.',
    expected: true,
  },
  {
    match: /earnings_already_paid/i,
    code: 'earnings_already_paid',
    userMessage: 'Hay comisiones ya pagadas. Anulá la liquidación primero y después reintentá.',
    expected: true,
  },
  {
    match: /earnings_already_settled/i,
    code: 'earnings_already_settled',
    userMessage: 'Hay comisiones en una liquidación abierta. Descartala y después reintentá.',
    expected: true,
  },
  {
    match: /Ya existe una liquidación activa/i,
    code: 'settlement_overlap',
    userMessage: 'Ya hay una liquidación abierta para ese período.',
    expected: true,
  },
  {
    match: /Solo se pueden anular liquidaciones pagadas/i,
    code: 'settlement_void_not_paid',
    userMessage: 'Solo se pueden anular liquidaciones pagadas. Si está en borrador, descartala.',
    expected: true,
  },
  {
    match: /Solo se pueden descartar liquidaciones/i,
    code: 'settlement_cancel_invalid_status',
    userMessage: 'Solo se pueden descartar liquidaciones en borrador o aprobadas.',
    expected: true,
  },
  {
    match: /Liquidación no encontrada/i,
    code: 'settlement_not_found',
    userMessage: 'No encontré esa liquidación. Refrescá e intentá de nuevo.',
    expected: true,
  },
  {
    match: /no está en draft|no está aprobada/i,
    code: 'settlement_bad_status',
    userMessage: 'La liquidación no está en el estado esperado. Refrescá e intentá de nuevo.',
    expected: true,
  },
  {
    match: /Caja requerida|caja no está abierta|Sin caja/i,
    code: 'cash_session_required',
    userMessage: 'Para pagar en efectivo, abrí una caja en la sucursal primero.',
    expected: true,
  },
  {
    match: /neto a pagar debe ser mayor/i,
    code: 'settlement_net_non_positive',
    userMessage: 'El neto a pagar tiene que ser mayor a cero para ese método.',
    expected: true,
  },
  {
    match: /Método de pago inválido/i,
    code: 'payment_method_invalid',
    userMessage: 'Elegí un método de pago válido.',
    expected: true,
  },
  {
    match: /Motivo requerido/i,
    code: 'reason_required',
    userMessage: 'Ingresá un motivo.',
    expected: true,
  },
  {
    match: /Plan no encontrado/i,
    code: 'plan_not_found',
    userMessage: 'No encontré ese plan. Refrescá e intentá de nuevo.',
    expected: true,
  },
  {
    match: /Empleado no encontrado/i,
    code: 'employee_not_found',
    userMessage: 'No encontramos a esa persona.',
    expected: true,
  },
  {
    match: /Sin organización|organization_required|not_authenticated/i,
    code: 'session_org_required',
    userMessage: 'Sesión inválida. Volvé a iniciar sesión.',
    expected: true,
  },
  {
    match: /branch_required|branch_not_in_organization/i,
    code: 'branch_required',
    userMessage: 'Falta la sucursal. Elegí una sucursal e intentá de nuevo.',
    expected: true,
  },
  {
    match: /Network request failed|Failed to fetch|timeout|ECONNREFUSED/i,
    code: 'network_error',
    userMessage: 'Sin conexión. Reintentá.',
    expected: true,
  },
];

export function mapCommissionRpcError(
  raw: string | undefined | null,
  fallback = 'No se pudo completar la operación de comisiones. Reintentá.',
): MappedCommissionError {
  const msg = (raw ?? '').trim();
  if (!msg) {
    return { code: 'commission_empty', userMessage: fallback, expected: true };
  }
  for (const row of COMMISSION_ERROR_MAP) {
    if (row.match.test(msg)) {
      return { code: row.code, userMessage: row.userMessage, expected: row.expected };
    }
  }
  // Mensaje español corto del backend (sin códigos técnicos)
  if (
    msg.length <= 160 &&
    !/^(PGRST|JWT|duplicate key|violates|permission denied|ERROR:)/i.test(msg) &&
    (/[áéíóúñ]/i.test(msg) || /\b(no |la |el |ya )/i.test(msg))
  ) {
    return { code: 'commission_rpc_message', userMessage: msg, expected: true };
  }
  return { code: 'commission_unknown', userMessage: fallback };
}

export function commissionErrorMessage(
  raw: string | undefined | null,
  fallback?: string,
): string {
  return mapCommissionRpcError(raw, fallback).userMessage;
}
